/* How much of an allocated-minute bundle has been used.

   A bar rather than a bare percentage because the question people actually ask
   is "am I close?", and a filled shape answers that before the number is read.

   The four states carry meaning and so take colours from the status tokens,
   not the brand ones: amber and red here mean the same thing they mean on a
   missed call. An allowance nobody has configured is drawn differently again —
   an empty rail rather than a full one — because "no data" and "none used" must
   not look the same. */
export default function UsageBar({
  measure,
  showLabel = true,
  coverage = null,
  title = null,
  /* Drops the "N min remaining" line. For a table cell, where the row is the
     unit of scanning and a third line of text per bundle turns a list into a
     wall. */
  compact = false,
}) {
  if (!measure) return null;

  const { percent, state, minutes, allowance, remaining_minutes: remaining } = measure;

  /* The coverage the bundle was sold as — UK, UK/EU, RO/EU/UK/US. An extension
     can hold more than one, so without this the bars are unlabelled and there
     is no telling which pool is nearly gone. */
  const tag = coverage
    ? <span className="usage__tag" title={title ?? undefined}>{coverage}</span>
    : null;

  if (state === 'unset') {
    return (
      <div className="usage">
        {showLabel && (
          <div className="usage__label">
            <span className="usage__used">{tag}{minutes} min</span>
            <span className="usage__cap usage__cap--muted">no allowance set</span>
          </div>
        )}
        <div className="usage__rail usage__rail--empty" />
      </div>
    );
  }

  if (state === 'unlimited') {
    return (
      <div className="usage">
        {showLabel && (
          <div className="usage__label">
            <span className="usage__used">{tag}{minutes} min</span>
            <span className="usage__cap">Unlimited</span>
          </div>
        )}
        <div className="usage__rail usage__rail--empty" />
      </div>
    );
  }

  // Over 100% still draws a full bar — the overage is told by the colour and
  // the number, and a bar spilling past its rail just looks like a bug.
  const width = Math.min(100, Math.max(0, percent ?? 0));

  return (
    <div className="usage">
      {showLabel && (
        <div className="usage__label">
          <span className="usage__used">
            {tag}{minutes} <span className="usage__unit">of {allowance.minutes} min</span>
          </span>
          <span className={`usage__pct usage__pct--${state}`}>{percent}%</span>
        </div>
      )}
      <div className="usage__rail">
        <div className={`usage__fill usage__fill--${state}`} style={{ width: `${width}%` }} />
      </div>
      {showLabel && !compact && (
        <div className="usage__foot">
          {state === 'over'
            ? 'Allowance used in full'
            : `${remaining} min remaining`}
        </div>
      )}
    </div>
  );
}
